import { useState } from 'react'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import {
  sendVerificationEmail,
  UPDATE_EMAIL_KEY,
  VERIFY_PASSWORD_KEY,
} from '@/actions/authActions'
import { selectErrorsForKeys } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

import { TypographyStyles } from '@/styles'
import { Alert, Button, Dialog, Icon, Text } from '@/ui'

interface ChangeEmailProps {
  isVerified: boolean
}

export const VerificationDialog = ({ isVerified }: ChangeEmailProps) => {
  const dispatch = useAppDispatch()
  const [reverifySent, setReverifySent] = useState(false)

  const errors = useReselector(selectErrorsForKeys, [
    VERIFY_PASSWORD_KEY,
    UPDATE_EMAIL_KEY,
  ])

  const resendEmail = async () => {
    // Resend then redirect to accounts page
    await sendVerificationEmail()(dispatch)
    setReverifySent(true)
  }

  const variants = {
    verified: {
      icon: solid('check'),
      color: 'bg-success',
      text: 'Thank you for verifying your email!',
    },
    reVerification: {
      icon: solid('envelope'),
      color: 'bg-neutral-900',
      text: 'We just sent you a new verification email! Click the link in your email to verify.',
    },
    expiredVerification: {
      icon: solid('exclamation-triangle'),
      color: 'bg-warning',
      text: 'It looks like this verification link has expired! Please request a new one by clicking &#39;Resend Email Verification&#39; below.',
    },
  }

  const variant = isVerified
    ? variants.verified
    : reverifySent
      ? variants.reVerification
      : variants.expiredVerification

  const closeText = isVerified ? 'Cancel' : 'Cancel and Verify Later'

  return (
    <Dialog>
      <Dialog.Trigger asChild>
        <Button variant="link" onClick={sendVerificationEmail} size="sm">
          <Text variant={TypographyStyles.LABEL_SM}>
            Resend verification email
          </Text>
        </Button>
      </Dialog.Trigger>

      <Dialog.Content size="sm">
        <Dialog.Title>Change Your Email Address</Dialog.Title>

        <Dialog.Description>
          To change your email address, enter your password and your new email
          address.
        </Dialog.Description>

        {Boolean(errors.length) && (
          <Alert variant="error">{errors[0].message}</Alert>
        )}

        <Icon icon={variant.icon} color={variant.color} />

        <Text variant={TypographyStyles.H1}>{variant.text}</Text>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button variant="ghost">{closeText}</Button>
          </Dialog.Close>

          {!isVerified && (
            <Button onClick={resendEmail}>Resend Email Verification</Button>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
